import { Button, ColumnTable, useMobile } from '@octano/global-ui';
import dayjs from 'dayjs';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ClassTypeLegend from '../../../components/ClassTypeLegend/ClassTypeLegend';
import useCurrentTimeSubscription from '../../../hooks/useCurrentTimeSubscription';
import useServerSettings from '../../../hooks/useServerSettings';
import { useUserState } from '../../../hooks/useUserState';
import { ActivityName } from '../../../types/Class';
import { SelectedClass } from '../parts/FutureClassesSearch';
import useTeacherClassManagementSetting from '../../../hooks/useTeacherClassManagementSetting';
import showModalsectionClosedAt from '../../../utils/showModalSectionClosedAt';

interface Props {
  onClassAdvance: (classData: SelectedClass) => void;
  onClassRecovery: (classData: SelectedClass) => void;
  onClassChangeClassroom: (classData: SelectedClass) => void;
}

function useColumn(props: Props): ColumnTable<SelectedClass>[] {
  const { onClassAdvance, onClassRecovery, onClassChangeClassroom } = props;

  const { t } = useTranslation();
  const isMobile = useMobile();
  const { userData } = useUserState();
  const { offset } = useServerSettings();
  const { data: teacherClassMgmt } = useTeacherClassManagementSetting();

  const currentTime = useCurrentTimeSubscription({
    offset,
    refreshEvery: 15000,
  });

  const canAdvanceClass = useCallback(
    (schedule: string, date: string) => {
      const [startTimeStr] = schedule.split(' - ');
      const [hours, minutes] = startTimeStr.split(':').map((n) => Number(n));
      const classStartTime = dayjs(date)
        .hour(hours)
        .minute(minutes)
        .second(0)
        .utcOffset(offset, true);

      return currentTime.isBefore(classStartTime);
    },
    [currentTime, offset],
  );

  return useMemo(() => {
    if (isMobile) {
      return [
        {
          headerText: t(`common.terms.date`),
          columnName: 'date',
        },
        {
          headerText: t(`common.terms.modules`),
          columnName: 'modules',
        },
        {
          headerText: '',
          columnName: 'action',
          width: '180px',
          cellFormat: (data) => {
            const { schedule, date } = data.row;
            const disableBtn = !canAdvanceClass(schedule, date);

            const buttons: JSX.Element[] = [];
            if (teacherClassMgmt?.updateClassroomAllowed) {
              buttons.push(
                <Button
                  key="modify"
                  text={t(`common.btnModifyClassroom`)}
                  outlined
                  onClick={() => onClassChangeClassroom(data.row)}
                  size="sm"
                  disabled={disableBtn}
                />,
              );
            }
            if (teacherClassMgmt?.rescheduleClassAllowed) {
              buttons.push(
                <Button
                  key="reschedule"
                  text={t(`common.btnReschedule`)}
                  outlined
                  onClick={() => {
                    onClassRecovery(data.row);
                  }}
                  size="sm"
                  className="mr-2"
                  disabled={disableBtn}
                />,
              );
            }
            buttons.push(
              <Button
                key="advance"
                text={t(`common.btnAdvanceClass`)}
                outlined
                onClick={() => onClassAdvance(data.row)}
                size="sm"
                disabled={disableBtn}
              />,
            );
            return <>{buttons}</>;
          },
        },
      ];
    } else {
      return [
        {
          headerText: t(`common.terms.date`),
          columnName: 'date',
        },
        {
          headerText: t(`common.terms.modules`),
          columnName: 'modules',
        },
        {
          headerText: t(`common.terms.schedule`),
          columnName: 'schedule',
        },
        {
          headerText: t(`common.terms.activity`),
          columnName: 'activity',
          cellFormat: (data) => (
            <ClassTypeLegend activityName={data.row.activity as ActivityName} />
          ),
        },
        {
          headerText: t(`common.terms.responsability`),
          columnName: '123',
          cellFormat: (data) => {
            const isHeadTeacher = data.row.professors.some(
              (p) => p.account.teacherId === userData?.teacherId,
            );
            return isHeadTeacher
              ? t(`common.terms.headTeacher`)
              : t(`common.terms.classResponsible`);
          },
        },
        {
          headerText: '',
          columnName: 'action',
          cellFormat: (data: any) => {
            const { schedule, date } = data.row;
            const disableBtn = !canAdvanceClass(schedule, date);
            const buttons: JSX.Element[] = [];
            if (teacherClassMgmt?.updateClassroomAllowed) {
              buttons.push(
                <Button
                  key="modify"
                  text={t(`common.btnModifyClassroom`)}
                  outlined
                  onClick={() => onClassChangeClassroom(data.row)}
                  size="sm"
                  className="mr-2"
                  disabled={disableBtn}
                />,
              );
            }
            if (teacherClassMgmt?.rescheduleClassAllowed) {
              buttons.push(
                <Button
                  key="reschedule"
                  text={t(`common.btnReschedule`)}
                  outlined
                  onClick={() => {
                    if (data.row.sectionClosedAt) {
                      showModalsectionClosedAt(t);
                    } else {
                      onClassRecovery(data.row);
                    }
                  }}
                  size="sm"
                  className="mr-2"
                  disabled={disableBtn}
                />,
              );
            }
            buttons.push(
              <Button
                key="advance"
                text={t(`common.btnAdvanceClass`)}
                onClick={() => onClassAdvance(data.row)}
                size="sm"
                disabled={disableBtn}
              />,
            );
            return <div className="d-flex justify-content-end">{buttons}</div>;
          },
        },
      ];
    }
  }, [
    isMobile,
    onClassAdvance,
    onClassRecovery,
    onClassChangeClassroom,
    canAdvanceClass,
    t,
    userData,
    teacherClassMgmt,
  ]);
}

export default useColumn;
